import type { PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import type { AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_LEVELS } from '@/domains/pages/ai-proficiency/constants';
import type { LevelMigrationSummary } from '@/domains/pages/ai-proficiency/fixtures/migration';

const LEVEL_SET = new Set<string>(AIFI_LEVELS);

/** Novice = 0 … Expert = 3 */
export const AIFI_LEVEL_RANK: Record<AifiLevel, number> = {
  Novice: 0,
  Beginner: 1,
  Practitioner: 2,
  Expert: 3,
};

const LEVELS_BY_RANK = [...AIFI_LEVELS].sort((a, b) => AIFI_LEVEL_RANK[a] - AIFI_LEVEL_RANK[b]);

export type ProficiencyTierCounts = Record<AifiLevel, { current: number; previous: number }>;

export function mapProficiencyTierCounts(data: PaginatedExtendedGroupByQuery): ProficiencyTierCounts {
  const counts: ProficiencyTierCounts = {
    Expert: { current: 0, previous: 0 },
    Practitioner: { current: 0, previous: 0 },
    Beginner: { current: 0, previous: 0 },
    Novice: { current: 0, previous: 0 },
  };

  for (const row of data.data) {
    const level = row.group_name;
    if (!LEVEL_SET.has(level)) continue;

    const tier = level as AifiLevel;
    counts[tier] = {
      current: row.n_users ?? 0,
      previous: row.n_users_prev ?? 0,
    };
  }

  return counts;
}

/**
 * Estimates tier movement between period snapshots from marginal tier counts
 * (current `n_users` vs previous-period `n_users_prev` per proficiency tier).
 *
 * Heuristic: only pairs adjacent tiers. Multi-tier jumps and unmatched hires/leavers
 * fall into `stayed`, which partitions current headcount with moved up / slipped back.
 */
export function computeProficiencyMigration(tierCounts: ProficiencyTierCounts): LevelMigrationSummary {
  const current = LEVELS_BY_RANK.map((level) => tierCounts[level].current);
  const previous = LEVELS_BY_RANK.map((level) => tierCounts[level].previous);
  const delta = current.map((value, index) => value - previous[index]);

  let movedUp = 0;
  let slippedBack = 0;

  // Bottom-up: when a lower tier loses headcount and the tier above gains, count moved up.
  for (let index = 0; index < LEVELS_BY_RANK.length - 1; index++) {
    if (delta[index] < 0 && delta[index + 1] > 0) {
      const flow = Math.min(-delta[index], delta[index + 1]);
      movedUp += flow;
      delta[index] += flow;
      delta[index + 1] -= flow;
    }
  }

  // Top-down: deficits at higher tiers flow into surpluses at lower tiers (downward movement).
  for (let index = LEVELS_BY_RANK.length - 1; index > 0; index--) {
    if (delta[index] < 0 && delta[index - 1] > 0) {
      const flow = Math.min(-delta[index], delta[index - 1]);
      slippedBack += flow;
      delta[index] += flow;
      delta[index - 1] -= flow;
    }
  }

  const totalCurrent = current.reduce((sum, value) => sum + value, 0);
  const stayed = Math.max(0, totalCurrent - movedUp - slippedBack);

  return { movedUp, slippedBack, stayed };
}

type ProficiencyMigrationRow = {
  aifi_proficiency_moved_up?: number | null;
  aifi_proficiency_slipped_back?: number | null;
  aifi_proficiency_stayed?: number | null;
};

export function mapProficiencyMigrationFromAggregateRow(
  row: ProficiencyMigrationRow | undefined,
): LevelMigrationSummary {
  return {
    movedUp: row?.aifi_proficiency_moved_up ?? 0,
    slippedBack: row?.aifi_proficiency_slipped_back ?? 0,
    stayed: row?.aifi_proficiency_stayed ?? 0,
  };
}

/** Uses backend per-user tier transitions (first vs last chart bucket). */
export function mapProficiencyMigrationFromAggregates(
  data: PaginatedExtendedGroupByQuery,
): LevelMigrationSummary {
  return mapProficiencyMigrationFromAggregateRow(data.data[0]);
}
